import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';
import { FirebaseService } from '../../../services/firebaseService';
import './customer.css';

function CustomerForm({ customerId, customerToEdit, onCustomerAdded, isEditMode = false }) {
  const { currentUser } = useAuth();
  const [form, setForm] = useState({
    customerName: '',
    customerContact: {
      phone: '',
      email: '',
      address: '',
      gstin: '',
    },
    company: '',
    createdBy: '',
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showFirecracker, setShowFirecracker] = useState(false);
  const [generatedCustomerId, setGeneratedCustomerId] = useState('');

  // Determine the customer ID to use (either from customerId prop or customerToEdit)
  const targetCustomerId = customerId || (customerToEdit ? (customerToEdit.id || customerToEdit._id) : null);

  // Generate customer ID for new customers
  const generateCustomerId = useCallback(async () => {
    if (!targetCustomerId) {
      try {
        // Try to get existing customers to determine next ID
        const existingCustomers = await FirebaseService.getCustomers();
        const lastCustomerId = existingCustomers.length > 0 
          ? Math.max(...existingCustomers.map(c => {
              const match = c.customerId?.match(/CUST-(\d+)/);
              return match ? parseInt(match[1]) : 0;
            }))
          : 0;
        const nextId = lastCustomerId + 1;
        const newCustomerId = `CUST-${nextId}`;
        setGeneratedCustomerId(newCustomerId);
        console.log('Generated customer ID:', newCustomerId);
      } catch (error) {
        console.log('Error generating customer ID, using fallback:', error);
        // Fallback to localStorage
        const storedCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
        const lastCustomerId = storedCustomers.length > 0 ? 
          Math.max(...storedCustomers.map(c => parseInt(c.customerId?.replace(/\D/g, '') || '0'))) : 0;
        const nextId = lastCustomerId + 1;
        const newCustomerId = `CUST-${nextId}`;
        setGeneratedCustomerId(newCustomerId);
        console.log('Generated customer ID (fallback):', newCustomerId);
      }
    }
  }, [targetCustomerId]);

  useEffect(() => {
    generateCustomerId();
  }, [generateCustomerId]);

  useEffect(() => {
    if (customerToEdit) {
      // If customerToEdit is provided, use it directly
      setForm(customerToEdit);
    } else if (targetCustomerId) {
      // Otherwise, fetch the customer by ID
      const fetchCustomer = async () => {
        try {
          if (!currentUser) throw new Error('No authentication token');
          const idToken = await currentUser.getIdToken(true);
          const res = await axios.get(`${API_CONFIG.BASE_URL}/api/customers/${targetCustomerId}`, {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          setForm(res.data);
        } catch (err) {
          alert('Failed to fetch customer: ' + err.message);
        }
      };
      fetchCustomer();
    }
  }, [customerToEdit, targetCustomerId, currentUser]);



  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = ${value}`);
    if (['phone', 'email', 'address', 'gstin'].includes(name)) {
      setForm({
        ...form,
        customerContact: { ...form.customerContact, [name]: value },
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to flip book
    
    try {
      const payload = {
        ...form,
        customerId: generatedCustomerId, // Use the generated customer ID
        createdBy: form.createdBy || currentUser?.email || 'admin',
        lastUpdatedBy: form.createdBy || currentUser?.email || 'admin',
        company: form.company || 'default_company',
      };
      
      console.log('CustomerForm: Submitting payload:', payload);
      console.log('CustomerForm: API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
      console.log('CustomerForm: USE_FIREBASE_ONLY:', API_CONFIG.USE_FIREBASE_ONLY);
      
      let success = false;
      let createdCustomer = null;
      
      // Try API first (only if not in Firebase-only mode)
      if (!API_CONFIG.USE_FIREBASE_ONLY && API_CONFIG.BASE_URL && currentUser) {
        try {
          const idToken = await currentUser.getIdToken(true);
          console.log('CustomerForm: Trying API submission...');
          
          if (targetCustomerId) {
            await axios.put(`${API_CONFIG.BASE_URL}/api/customers/${targetCustomerId}`, payload, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            console.log('✅ CustomerForm: Customer updated via API');
            setSuccessMessage('Customer updated successfully! 🎉');
          } else {
            const response = await axios.post(`${API_CONFIG.BASE_URL}/api/customers`, payload, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            createdCustomer = response.data;
            console.log('✅ CustomerForm: Customer created via API:', createdCustomer.customerId);
            setSuccessMessage(`Customer added successfully! 🎉 (ID: ${generatedCustomerId})`);
          }
          success = true;
        } catch (apiError) {
          console.log('⚠️ CustomerForm: API submission failed:', apiError.message);
          // Continue to Firebase fallback
        }
      }
      
      // Try Firebase if API failed or not available
      if (!success) {
        try {
          console.log('🔄 CustomerForm: Trying Firebase submission...');
          
          if (targetCustomerId) {
            // Update existing customer in Firebase
            const updateSuccess = await FirebaseService.updateCustomer(targetCustomerId, payload);
            if (updateSuccess) {
              console.log('✅ CustomerForm: Customer updated via Firebase');
              setSuccessMessage('Customer updated successfully! 🎉');
              success = true;
            }
          } else {
            // Create new customer in Firebase
            createdCustomer = await FirebaseService.addCustomer(payload);
            console.log('✅ CustomerForm: Customer created via Firebase:', createdCustomer.customerId);
            setSuccessMessage(`Customer added successfully! 🎉 (ID: ${generatedCustomerId})`);
            success = true;
          }
        } catch (firebaseError) {
          console.log('⚠️ CustomerForm: Firebase submission failed:', firebaseError.message);
        }
      }
      
      // Final fallback to localStorage for new customers only
      if (!success && !targetCustomerId) {
        try {
          console.log('🔄 CustomerForm: Trying localStorage fallback...');
          
          // Get existing customers from localStorage
          const existingCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
          
          createdCustomer = {
            ...payload,
            id: generatedCustomerId, // Use the generated customer ID
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          // Save to localStorage
          existingCustomers.push(createdCustomer);
          localStorage.setItem('customers', JSON.stringify(existingCustomers));
          
          console.log('✅ CustomerForm: Customer created in localStorage:', createdCustomer.customerId);
          setSuccessMessage(`Customer added successfully! 🎉 (ID: ${generatedCustomerId})`);
          success = true;
        } catch (storageError) {
          console.log('⚠️ CustomerForm: localStorage fallback failed:', storageError.message);
        }
      }
      
      if (success) {
        // Show success effects
        setShowFirecracker(true);
        setTimeout(() => setShowFirecracker(false), 2000);
        
        // Reset form
        setForm({
          customerName: '',
          customerContact: { phone: '', email: '', address: '', gstin: '' },
          company: '',
          createdBy: '',
        });
        
        // Generate new customer ID for next customer
        generateCustomerId();
        
        // Clear success message after delay
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Notify parent component
        if (onCustomerAdded) {
          onCustomerAdded();
        }
      } else {
        throw new Error('Failed to save customer to any storage method');
      }
      
    } catch (err) {
      console.error('❌ CustomerForm: Submission error:', err);
      
      let errorMessage = 'Unknown error occurred';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Handle specific error cases
      if (err.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        errorMessage = 'Network error. Saved to local storage instead.';
      }
      
      alert(`Error saving customer: ${errorMessage}`);
    }
  };

  return (
    <div className="customer-form-container">
      {showFirecracker && (
        <div className="firecracker-overlay">
          <div className="firecracker-particle particle-1"></div>
          <div className="firecracker-particle particle-2"></div>
          <div className="firecracker-particle particle-3"></div>
          <div className="firecracker-particle particle-4"></div>
          <div className="firecracker-particle particle-5"></div>
        </div>
      )}
      
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="customer-form">
        
        {!targetCustomerId && (
          <div className="form-group">
            <label>Customer ID:</label>
            <input
              type="text"
              value={generatedCustomerId || "Generating..."}
              readOnly
              className="form-control"
              style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
            />
            <small className="form-text">Customer ID is automatically generated</small>
          </div>
        )}

        {targetCustomerId && (
          <div className="form-group">
            <label>Customer ID:</label>
            <input
              type="text"
              value={form.customerId || 'N/A'}
              readOnly
              className="form-control"
              style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
            />
            <small className="form-text">Existing customer ID (cannot be changed)</small>
          </div>
        )}

        <div className="form-group">
          <label>Customer Name *</label>
          <input
            type="text"
            name="customerName"
            value={form.customerName}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              name="phone"
              value={form.customerContact.phone}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={form.customerContact.email}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group form-full-width">
          <label>Address</label>
          <textarea
            name="address"
            value={form.customerContact.address}
            onChange={handleChange}
            className="form-control"
            rows="3"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>GSTIN</label>
            <input
              type="text"
              name="gstin"
              value={form.customerContact.gstin}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Company</label>
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <button type="submit" className="submit-btn">
          {targetCustomerId ? 'Update Customer' : 'Add Customer'}
        </button>
      </form>
    </div>
  );
}

export default CustomerForm;